import { computed } from 'vue';

import { IN_BROWSER } from '@components/utils';

import type { NTable } from '../interface';
import type { ComputedRef, Ref } from 'vue';

export interface IUseSticky {
  isSticky: boolean;
  offsetHeader: number;
  offsetSummary: number;
  offsetScroll: number;
  stickyClassName: string;
  container: Window | HTMLElement | null;
}

// SSR support
const defaultContainer = IN_BROWSER ? window : null;

export function useSticky(stickyRef: Ref<boolean | NTable.NSticky.ISticky | undefined>): ComputedRef<IUseSticky> {
  return computed(() => {
    const {
      offsetHeader = 0,
      offsetSummary = 0,
      offsetScroll = 0,
      getContainer = () => defaultContainer
    } = typeof stickyRef.value === 'object' ? stickyRef.value : {};

    const container = getContainer() || defaultContainer;
    const isSticky = !!stickyRef.value;

    return {
      isSticky,
      stickyClassName: isSticky ? 'cds-table__sticky-holder' : '',
      offsetHeader,
      offsetSummary,
      offsetScroll,
      container
    };
  });
}

export function useStickyOffsets(colWidthsRef: ComputedRef<(number | undefined)[]>, columnCountRef: Ref<number>) {
  const stickyOffsets: ComputedRef<NTable.NSticky.IStickyOffsets> = computed(() => {
    const leftOffsets: number[] = [];
    const rightOffsets: number[] = [];
    let left = 0;
    let right = 0;

    const colWidths = colWidthsRef.value;
    const columnCount = columnCountRef.value;

    for (let start = 0; start < columnCount; start += 1) {
      leftOffsets[start] = left;
      left += colWidths[start] || 0;

      const end = columnCount - start - 1;
      rightOffsets[end] = right;
      right += colWidths[end] || 0;
    }

    return {
      left: leftOffsets,
      right: rightOffsets
    };
  });

  return stickyOffsets;
}
